import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';

import SEO from '../../components/SEO';

const ServiceAppartement = () => {
  const advantages = [
    'Équipe formée pour les accès difficiles',
    'Protection des parties communes',
    'Matériel adapté (sangles, diables, protection)',
    'Respect des horaires et du voisinage',
    'Intervention même sans ascenseur',
    'Devis gratuit et sans engagement'
  ];

  const situations = [
    {
      title: 'Appartement en étage sans ascenseur',
      description: 'Notre équipe est équipée et formée pour les interventions dans les étages élevés sans ascenseur.'
    },
    {
      title: 'Copropriété avec accès restreint',
      description: 'Nous respectons les règles de copropriété et protégeons les parties communes lors de nos interventions.'
    },
    {
      title: 'Studio ou petit appartement',
      description: 'Même pour les petites surfaces, nous assurons un service professionnel et rapide.'
    },
    {
      title: 'Grand appartement à vider',
      description: 'Nos équipes peuvent vider intégralement des appartements de grande surface en une seule intervention.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras d'appartement en Haute-Savoie | DEBECO"
        description="Débarras d'appartement professionnel en Savoie et Haute-Savoie. Intervention même sans ascenseur, protection des parties communes."
        canonical="/services/appartement"
        ogImage="/images/appartement.jpg"
        jsonLd={[
          {"@context":"https://schema.org","@type":"Service","name":"Débarras d'appartement","description":"Débarras professionnel d'appartement, même sans ascenseur. Protection des parties communes et évacuation complète.","provider":{"@type":"LocalBusiness","name":"DEBECO","telephone":"+33451401617","address":{"@type":"PostalAddress","addressLocality":"Annecy","postalCode":"74600","addressCountry":"FR"}},"areaServed":[{"@type":"AdministrativeArea","name":"Haute-Savoie"},{"@type":"AdministrativeArea","name":"Savoie"},{"@type":"AdministrativeArea","name":"Ain"}]},
          {"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Services","item":"https://debeco.fr/services"},{"@type":"ListItem","position":3,"name":"Débarras d'appartement"}]}
        ]}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Débarras d'appartement</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Intervention efficace même dans les lieux difficiles d'accès
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/appartement.jpg"
                  webpSrc="/images/appartement.webp"
                  alt="Appartement en cours de vidage par DEBECO, intervention de débarras dans un immeuble en étage"
                  className="w-full h-full object-cover"
                  width="800"
                  height="1000"
                  loading="eager"
                  data-testid="service-appartement-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Building2 className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Intervention même sans ascenseur</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Le débarras d'appartement présente souvent des défis particuliers : étages élevés, absence d'ascenseur, escaliers étroits, règles de copropriété... Notre équipe est spécialement formée et équipée pour gérer toutes ces contraintes.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Nous utilisons du matériel adapté (sangles de portage, diables, housses de protection) et veillons à protéger les parties communes de l'immeuble. Nos interventions respectent les horaires de la copropriété et le voisinage.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Advantages */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Les avantages de notre service</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Situations */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Nos types d'interventions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {situations.map((situation, index) => (
                  <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3">{situation.title}</h3>
                      <p className="text-gray-600">{situation.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Nos interventions par ville */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-4 text-center">Nos interventions par ville</h2>
            <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
              Consultez nos pages dédiées au débarras d'appartement dans les principales villes de notre zone.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Link to="/debarras-appartement-annecy" className="group">
                <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                  <CardContent className="p-5 flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                      <Building2 className="w-5 h-5 text-blue-900 group-hover:text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-900 group-hover:text-emerald-700 transition-colors">Annecy</h3>
                      <p className="text-gray-500 text-sm">Haute-Savoie (74)</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-emerald-600 transition-colors" />
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Process */}
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-blue-900 mb-6 text-center">Notre méthode de travail</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[
                    { step: '1', title: 'Évaluation', desc: 'Visite pour évaluer l\'accès et le volume' },
                    { step: '2', title: 'Organisation', desc: 'Préparation du matériel adapté' },
                    { step: '3', title: 'Débarras', desc: 'Évacuation soignée et rapide' },
                    { step: '4', title: 'Nettoyage', desc: 'Appartement propre et vide' }
                  ].map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <span className="text-xl font-bold text-white">{item.step}</span>
                      </div>
                      <h3 className="font-bold text-blue-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos autres services de débarras</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/services/cave-grenier" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras de cave et grenier</p>
              <p className="text-sm text-gray-600 mt-1">Videz aussi vos espaces de stockage</p>
            </Link>
            <Link to="/debarras-appartement-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras d'appartement à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Intervention spécialisée dans le bassin annécien</p>
            </Link>
            <Link to="/devis" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Estimation gratuite en ligne</p>
              <p className="text-sm text-gray-600 mt-1">Obtenez un devis en quelques minutes</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras d'appartement ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit, même pour les accès difficiles
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceAppartement;
